import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { LogOut, Inbox, UserRound, Menu, X, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { api } from "@/lib/api";
import AuthModal from "@/components/AuthModal";

const LINKS = [
  { to: "/", label: "Who We Are", testid: "nav-link-home" },
  { to: "/organizer", label: "The Organizer", testid: "nav-link-organizer" },
  { to: "/websites", label: "Websites", testid: "nav-link-websites", page: "websites" },
  { to: "/social", label: "Social Media", testid: "nav-link-social", page: "social" },
  { to: "/2-0", label: "2.0", testid: "nav-link-two-point-oh", page: "two_point_oh" },
];

function UnreadBadge({ unread, className = "" }) {
  if (!unread) return null;
  return (
    <span className={`min-w-[20px] h-5 px-1.5 rounded-full bg-[#C48A9F] text-[#FFFDF7] text-[10px] font-bold flex items-center justify-center shadow-[0_0_0_2px_#FFFDF7] ${className}`} data-testid="nav-unread-badge">
      {unread > 99 ? "99+" : unread}
    </span>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { pages } = useSite();
  const links = LINKS.filter((l) => !l.page || pages[l.page]);
  const [authOpen, setAuthOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const [unread, setUnread] = useState(0);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  useEffect(() => {
    if (!user?.admin) { setUnread(0); return; }
    const fetchUnread = () => api.get("/admin/inquiries/unread-count").then((r) => setUnread(r.data.unread)).catch(() => {});
    fetchUnread();
    const id = setInterval(fetchUnread, 45000);
    const onRead = () => setUnread(0);
    window.addEventListener("inquiries-read", onRead);
    return () => { clearInterval(id); window.removeEventListener("inquiries-read", onRead); };
  }, [user, location.pathname]);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 glass" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="nav-brand">
            <img src="/logo-transparent.png" alt="Business Aligned" className="w-11 h-11 object-contain" />
            <span className="f-display tracking-tight text-[20px] leading-none">Business <span className="f-serif text-[#A8785A]">Aligned</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-7">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={l.testid}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-3 shrink-0">
            {user ? (
              <>
                {user.admin && (
                  <Link to="/admin" data-testid="nav-admin-link"
                    className="relative flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] border border-[#A8785A] text-[#A8785A] rounded-full px-4 py-2 hover:bg-[#A8785A] hover:text-[#FFFDF7] transition-colors">
                    <Inbox size={13} /> Inbox
                    <UnreadBadge unread={unread} className="absolute -top-2 -right-2" />
                  </Link>
                )}
                <Link to="/account" data-testid="nav-account-link" className="flex items-center gap-1.5 f-mono text-xs text-[#8C7562] hover:text-[#3A2E25] transition-colors">
                  <UserRound size={13} /> @{user.username}
                </Link>
                <button onClick={logout} data-testid="nav-logout-btn"
                  className="flex items-center gap-1.5 text-xs font-semibold border border-[#E8DCCB] bg-[#FFFDF7] rounded-full px-4 py-2 hover:border-[#A8785A] hover:text-[#A8785A] transition-colors">
                  <LogOut size={13} /> Logout
                </button>
              </>
            ) : (
              <button onClick={() => setAuthOpen(true)} data-testid="nav-login-btn" className="btn-primary !py-2 !px-5 !text-xs">
                Log In
              </button>
            )}
          </div>
          <div className="md:hidden flex items-center gap-2 shrink-0">
            {user?.admin && (
              <Link to="/admin" data-testid="nav-admin-link-mobile" aria-label="Inbox"
                className="relative w-10 h-10 rounded-full border border-[#A8785A] text-[#A8785A] flex items-center justify-center">
                <Inbox size={15} />
                <UnreadBadge unread={unread} className="absolute -top-1.5 -right-1.5" />
              </Link>
            )}
            {!user && (
              <button onClick={() => setAuthOpen(true)} data-testid="nav-login-btn" className="btn-primary !py-2 !px-4 !text-[11px]">
                Log In
              </button>
            )}
            <button onClick={() => setMenuOpen((o) => !o)} data-testid="nav-menu-toggle" aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              className="w-10 h-10 rounded-full border border-[#E8DCCB] bg-[#FFFDF7] flex items-center justify-center text-[#3A2E25] hover:border-[#A8785A] transition-colors">
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div key="mobile-menu" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 md:hidden bg-[#FAF6EF] blush-wash grain flex flex-col pt-24 pb-10 px-7 overflow-y-auto"
            data-testid="mobile-menu">
            <nav className="flex flex-col">
              {links.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 + i * 0.06, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>
                  <NavLink to={l.to} data-testid={`${l.testid}-mobile`}
                    className={({ isActive }) => `flex items-center justify-between py-5 border-b border-[#E8DCCB] f-display text-3xl tracking-tight ${isActive ? "text-[#A8785A]" : "text-[#3A2E25]"}`}>
                    {l.label} <ArrowRight size={18} className="text-[#C39B7B]" />
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.4 }} className="mt-8 flex flex-col gap-3">
              {user ? (
                <>
                  <Link to="/account" data-testid="nav-account-link-mobile" className="btn-ghost justify-center">
                    <UserRound size={14} /> My account · @{user.username}
                  </Link>
                  {user.admin && (
                    <Link to="/admin" className="btn-ghost justify-center"><Inbox size={14} /> Owner inbox{unread > 0 ? ` (${unread})` : ""}</Link>
                  )}
                  <button onClick={() => { setMenuOpen(false); logout(); }} data-testid="nav-logout-btn-mobile" className="btn-primary justify-center">
                    <LogOut size={14} /> Logout
                  </button>
                </>
              ) : (
                <button onClick={() => { setMenuOpen(false); setAuthOpen(true); }} data-testid="nav-login-btn-mobile" className="btn-primary justify-center">
                  Log in or create an account
                </button>
              )}
              <p className="eyebrow text-center mt-6">Business Aligned</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </>
  );
}
